import {z} from 'zod/v4';

import {ConditionSchema} from './condition';
import {ArrayFilterSchema} from './filter';
import {ReferenceQuerySchema, ReferenceSchema} from './reference';

export const SwitchSchema = <T>(schema: z.ZodType<T>) =>
  z.object({
    $switch: z.array(
      z.union([
        z.object({
          $default: schema,
        }),
        z.object({
          $case: z.array(ConditionSchema),
          $then: schema,
        }),
      ]),
    ),
  });

export const supportsSwitch = <T>(schema: z.ZodType<T>) => z.union([schema, SwitchSchema(schema)]);

export const ConditionalSchema = <T>(schema: z.ZodType<T>) =>
  z.strictObject({
    $if: z.array(ConditionSchema),
    $then: schema,
  });

export const supportsConditional = <T>(schema: z.ZodType<T>) => z.union([schema, ConditionalSchema(schema)]);

export const GeneratedListSchema = <T>(schema: z.ZodType<T>) =>
  z.object({
    $generated: z.union([
      z.strictObject({
        $repeat: z.union([ReferenceSchema, ReferenceQuerySchema]),
        value: supportsSwitch(schema),
      }),
      z.strictObject({
        $each: ReferenceQuerySchema,
        useValueAsIndex: z.boolean().optional(),
        value: supportsSwitch(schema),
      }),
      z.strictObject({
        $list: z.array(supportsConditional(schema)),
      }),
    ]),
  });

export const GeneratedSumSchema = z.object({
  $generated: z.strictObject({
    $sum: z.union([
      z.object({
        fields: z.array(z.string()),
        property: z.string().optional(),
        keepDecimals: z.boolean().optional(),
      }),
      z.object({
        array: z.strictObject({
          count: z.array(
            z.union([
              z.string(),
              z.strictObject({
                array: z.string(),
                filters: z.lazy(() => z.array(ArrayFilterSchema)).optional(),
                groupBy: z.string().optional(),
              }),
            ]),
          ),
        }),
      }),
    ]),
  }),
});

export const GeneratedFirstSchema = z.object({
  $generated: z.strictObject({
    $first: z.strictObject({
      array: z.string(),
      field: z.string(),
      filters: z.lazy(() => z.array(ArrayFilterSchema)).optional(),
    }),
  }),
});

export const GeneratedValueSchema = z.union([GeneratedSumSchema, GeneratedFirstSchema]);

const StripOptionsSchema = z.union([
  z.string(),
  z.number(),
  z.strictObject({
    chars: z.string().optional(),
    first: z.number().nonnegative().optional(),
    last: z.number().nonnegative().optional(),
  }),
]);

export const NullOperationSchema = z.object({
  castValueAsNull: z.lazy(() => z.union([z.string(), ReferenceSchema])),
});

export const FormatOperationSchema = z.object({
  format: z.string(),
});

export const ReplaceOperationSchema = z.object({
  replace: z.array(z.string()),
});

export const StringOperationSchema = z.object({
  strip: StripOptionsSchema,
});

export const DateOperationSchema = z.object({
  stripDateYearPrefix: z.boolean(),
});
